/**
 * The employee's notification inbox — what the bell in `EmployeeTopBar`
 * shows.
 *
 * Self-contained on purpose: the bell lives in the app chrome, visible from
 * My Pay as well as My Leave, so it cannot depend on either screen's state.
 * It resolves its own employer link the same way `use_my_pay.ts` does, then
 * reads the notices from the EMPLOYER's tenant DO through the employer-link
 * grant (`personalPayroll.listMyLeaveNotifications`) — never a copy held
 * locally. See `services/payroll/leave_decision_notification_service.ts`.
 *
 * ── When it refreshes ──────────────────────────────────────────────────────
 * On mount, and whenever the app comes back to the foreground
 * (`visibilitychange`). Deliberately NOT on a timer: a poll would spend
 * battery and API calls all day for an event that happens a handful of times
 * a month, and a phone app is backgrounded far more often than it is left
 * open. Returning to the app is the moment an employee would look.
 *
 * This is an in-app inbox, not a push notification: nothing arrives while the
 * app is closed. Real push needs FCM/APNs credentials and a device-token
 * store — tracked separately.
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';

/** One decision notice, as the bell renders it. */
export interface EmployeeNotification {
  id: string;
  type: 'leave_request_approved' | 'leave_request_rejected';
  created_at: string;
  template_vars: Record<string, string | undefined>;
  /**
   * Which employer's tenant this row lives in. Carried per notice because an
   * employee can hold links to several employers and `markNotificationRead`
   * routes by entity — dismissing against the wrong one silently matches
   * nothing, and the notice comes back on the next refresh.
   */
  entity_id: string;
}

export interface UseEmployeeNotificationsResult {
  /** Newest first. Empty while loading, or when there is nothing to show. */
  notifications: EmployeeNotification[];
  /** Badge count — the number of undismissed notices. */
  unread_count: number;
  /** Dismiss one notice (optimistic; the server call follows). */
  dismiss: (notification_id: string) => Promise<void>;
  /** Re-read now. */
  refresh: () => void;
}

interface EmployerLinkRow {
  entity_id: string;
}

export function useEmployeeNotifications(): UseEmployeeNotificationsResult {
  const [entity_ids, set_entity_ids] = React.useState<string[]>([]);
  const [notifications, set_notifications] = React.useState<
    EmployeeNotification[]
  >([]);
  const [reload_token, set_reload_token] = React.useState(0);

  // Phase 1 — EVERY active employer, not just the first.
  //
  // An employee can hold links to more than one employer (My Pay has a
  // switcher for exactly that). Reading only `links[0]` meant the bell showed
  // one company's decisions while the employee was looking at another's pay,
  // and the second employer's approvals never surfaced at all. The chrome is
  // not scoped to a selected employer, so it carries all of them.
  React.useEffect(() => {
    let cancelled = false;
    async function resolve_link(): Promise<void> {
      try {
        const result =
          (await getTrpcClient().personalPayroll.listEmployerLinks.query()) as {
            links: EmployerLinkRow[];
          };
        if (cancelled) return;
        set_entity_ids(result.links.map((link) => link.entity_id));
      } catch {
        // An employee with no link (or a failed lookup) simply has no bell
        // content. Never surfaced as an error in the chrome: the app's own
        // screens already report their load failures, and a broken bell must
        // not look like a broken app.
        if (!cancelled) set_entity_ids([]);
      }
    }
    void resolve_link();
    return () => {
      cancelled = true;
    };
  }, []);

  // Phase 2 — the notices, across every employer, newest first.
  //
  // `entity_key` (not the array) is the dependency: a fresh array with the
  // same contents on every render would otherwise re-fire this effect
  // forever.
  const entity_key = entity_ids.join(',');
  React.useEffect(() => {
    if (entity_ids.length === 0) {
      set_notifications([]);
      return;
    }
    let cancelled = false;
    async function load(): Promise<void> {
      const client = getTrpcClient();
      const results = await Promise.all(
        entity_ids.map(async (entity_id) => {
          try {
            const result =
              (await client.personalPayroll.listMyLeaveNotifications.query({
                entity_id,
              })) as {
                notifications: Omit<EmployeeNotification, 'entity_id'>[];
              };
            // Stamp the employer on each row: `dismiss` needs it to route the
            // write back to the tenant the row actually lives in.
            return result.notifications.map((n) => ({ ...n, entity_id }));
          } catch {
            // One unreachable employer must not blank the whole bell — the
            // other employers' decisions are still worth showing.
            return [];
          }
        }),
      );
      if (cancelled) return;
      set_notifications(
        results.flat().sort((a, b) => b.created_at.localeCompare(a.created_at)),
      );
    }
    void load();
    return () => {
      cancelled = true;
    };
    // Depends on `entity_key`, the stable string projection of `entity_ids`
    // — depending on the array itself would re-fire on every render.
  }, [entity_key, reload_token]);

  const refresh = React.useCallback(() => {
    set_reload_token((t) => t + 1);
  }, []);

  // Coming back to the foreground is when an employee would look, so that is
  // when this re-reads. `visibilitychange` covers both the app being
  // backgrounded on a phone and the browser tab being switched away from.
  React.useEffect(() => {
    function on_visible(): void {
      if (document.visibilityState === 'visible') refresh();
    }
    document.addEventListener('visibilitychange', on_visible);
    return () => {
      document.removeEventListener('visibilitychange', on_visible);
    };
  }, [refresh]);

  const dismiss = React.useCallback(
    async (notification_id: string): Promise<void> => {
      // Routed by the notice's OWN employer, not one ambient entity: with
      // two employers linked, dismissing against the wrong tenant silently
      // matches nothing and the notice returns on the next refresh.
      const target = notifications.find((n) => n.id === notification_id);
      if (target === undefined) return;
      set_notifications((current) =>
        current.filter((n) => n.id !== notification_id),
      );
      try {
        await getTrpcClient().personalPayroll.markMyLeaveNotificationRead.mutate(
          { entity_id: target.entity_id, notification_id },
        );
      } catch (err) {
        // Ids only, never leave details (`docs/security-rules.md`).
        console.error(
          '[wagepro-web] dismissing a notification failed:',
          notification_id,
          err,
        );
      }
    },
    [notifications],
  );

  return {
    notifications,
    unread_count: notifications.length,
    dismiss,
    refresh,
  };
}
